import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { RefreshCw, Trash2, Ticket, Plus, Copy, CheckCircle2, Star } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminCoupons({ password }) {
  const headers = useMemo(() => ({ "X-Admin-Password": password }), [password]);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [copied, setCopied] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/checkout/admin/coupons`, { headers });
      setCoupons(data.items || []);
    } catch {
      toast.error("Failed to load coupons");
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const remove = async (code) => {
    if (!window.confirm(`Delete coupon ${code}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/checkout/admin/coupons/${encodeURIComponent(code)}`, { headers });
      setCoupons((cs) => cs.filter((c) => c.code !== code));
      toast.success("Coupon deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const toggleFeatured = async (coupon) => {
    const next = !coupon.featured;
    try {
      const { data } = await axios.patch(
        `${API}/checkout/admin/coupons/${encodeURIComponent(coupon.code)}`,
        { featured: next },
        { headers },
      );
      setCoupons((cs) => cs.map((c) => {
        if (c.code === data.code) return data;
        if (next && c.featured) return { ...c, featured: false };  // only one at a time
        return c;
      }));
      toast.success(next ? `${coupon.code} is now featured on the Shop landing.` : `${coupon.code} un-featured.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update coupon");
    }
  };

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(code);
      setTimeout(() => setCopied(null), 1500);
    } catch { /* noop */ }
  };

  return (
    <div data-testid="admin-coupons">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="eyebrow gold-bar">Print Shop</div>
          <h2 className="font-display text-3xl md:text-4xl mt-2 tracking-tight">Coupons</h2>
          <p className="text-white/50 text-sm mt-2">
            {coupons.length} coupon{coupons.length !== 1 ? "s" : ""} · {coupons.filter((c) => c.active).length} active
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} data-testid="coupons-refresh" className="btn-ghost !py-3 !px-4">
            <RefreshCw size={14} /> Reload
          </button>
          <button
            onClick={() => setShowNew(true)}
            data-testid="coupons-new-btn"
            className="btn-gold !py-3 !px-4"
          >
            <Plus size={14} /> New coupon
          </button>
        </div>
      </div>

      {loading ? (
        <div className="card-dark p-16 text-center text-white/50">Loading coupons…</div>
      ) : coupons.length === 0 ? (
        <div className="card-dark p-16 text-center text-white/50" data-testid="coupons-empty">
          <Ticket size={28} className="mx-auto text-gold/50 mb-4" />
          No coupons yet — create one to run promotions.
        </div>
      ) : (
        <div className="card-dark overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.02] text-[10px] uppercase tracking-[0.2em] text-white/40">
              <tr>
                <th className="text-left px-6 py-4">Code</th>
                <th className="text-left px-6 py-4">Discount</th>
                <th className="text-left px-6 py-4">Uses</th>
                <th className="text-left px-6 py-4">Expires</th>
                <th className="text-left px-6 py-4">Status</th>
                <th className="text-center px-6 py-4">Featured</th>
                <th className="text-right px-6 py-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {coupons.map((c) => (
                <tr key={c.code} data-testid={`coupon-row-${c.code}`} className="hover:bg-white/[0.015]">
                  <td className="px-6 py-4">
                    <button
                      onClick={() => copyCode(c.code)}
                      className="inline-flex items-center gap-2 font-mono text-gold hover:text-white transition-colors"
                      data-testid={`coupon-copy-${c.code}`}
                      title="Copy code"
                    >
                      {c.code}
                      {copied === c.code ? <CheckCircle2 size={12} /> : <Copy size={12} className="opacity-60" />}
                    </button>
                  </td>
                  <td className="px-6 py-4 text-white/85">
                    {c.kind === "percent" ? `${c.value}%` : `$${Number(c.value).toFixed(2)}`}
                  </td>
                  <td className="px-6 py-4 text-white/70">
                    {c.uses || 0}{c.max_uses ? ` / ${c.max_uses}` : ""}
                  </td>
                  <td className="px-6 py-4 text-white/60">
                    {c.expires_at ? new Date(c.expires_at).toLocaleDateString("en-CA") : "—"}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 border ${c.active ? "bg-gold/20 text-gold border-gold/50" : "bg-white/10 text-white/50 border-white/15"}`}>
                      {c.active ? "active" : "inactive"}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <button
                      onClick={() => toggleFeatured(c)}
                      data-testid={`coupon-featured-${c.code}`}
                      aria-label={c.featured ? "Un-feature this coupon" : "Feature this coupon on the Shop banner"}
                      title={c.featured ? "Currently shown as promo banner on /shop" : "Show as promo banner on /shop"}
                      className={`inline-flex items-center justify-center p-1.5 rounded-full transition-colors ${c.featured ? "text-gold hover:text-white" : "text-white/30 hover:text-gold"}`}
                    >
                      <Star size={16} fill={c.featured ? "currentColor" : "none"} strokeWidth={c.featured ? 1.5 : 2} />
                    </button>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => remove(c.code)}
                      data-testid={`coupon-delete-${c.code}`}
                      className="text-white/50 hover:text-red-400 transition-colors p-2"
                      aria-label="Delete coupon"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showNew && (
        <NewCouponModal
          password={password}
          onClose={() => setShowNew(false)}
          onCreated={(coupon) => {
            setCoupons((cs) => [coupon, ...cs]);
            setShowNew(false);
          }}
        />
      )}
    </div>
  );
}


function NewCouponModal({ password, onClose, onCreated }) {
  const [code, setCode] = useState("");
  const [kind, setKind] = useState("percent");
  const [value, setValue] = useState("");
  const [maxUses, setMaxUses] = useState("");
  const [expires, setExpires] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim() || code.trim().length < 2) {
      toast.error("Coupon code must be at least 2 characters.");
      return;
    }
    const numValue = parseFloat(value);
    if (!Number.isFinite(numValue) || numValue <= 0) {
      toast.error("Enter a positive discount value.");
      return;
    }
    if (kind === "percent" && numValue > 100) {
      toast.error("Percentage cannot exceed 100.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        code: code.trim().toUpperCase(),
        kind,
        value: numValue,
        active: true,
      };
      if (maxUses) payload.max_uses = parseInt(maxUses, 10) || null;
      if (expires) payload.expires_at = new Date(expires).toISOString();
      const { data } = await axios.post(
        `${API}/checkout/admin/coupons`,
        payload,
        { headers: { "X-Admin-Password": password } },
      );
      toast.success(`Coupon "${data.code}" created.`);
      onCreated(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create coupon");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="new-coupon-modal"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-md card-dark p-8"
      >
        <div className="eyebrow gold-bar">Promotion</div>
        <h2 className="font-display text-2xl mt-2 tracking-tight">New coupon</h2>

        <label className="block mt-6">
          <span className="block text-[10px] uppercase tracking-[0.22em] text-white/55 mb-2">Code <span className="text-gold">*</span></span>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            required
            minLength={2}
            data-testid="coupon-code-input"
            placeholder="SUMMER10"
            className="w-full bg-ink-800 border border-white/10 text-white px-4 py-3 text-sm rounded-sm outline-none focus:border-gold font-mono uppercase"
          />
        </label>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.22em] text-white/55 mb-2">Type</span>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              data-testid="coupon-kind-input"
              className="w-full bg-ink-800 border border-white/10 text-white px-4 py-3 text-sm rounded-sm outline-none focus:border-gold"
            >
              <option value="percent">Percent (%)</option>
              <option value="fixed">Fixed ($)</option>
            </select>
          </label>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.22em] text-white/55 mb-2">Value <span className="text-gold">*</span></span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              required
              data-testid="coupon-value-input"
              placeholder={kind === "percent" ? "10" : "25.00"}
              className="w-full bg-ink-800 border border-white/10 text-white px-4 py-3 text-sm rounded-sm outline-none focus:border-gold"
            />
          </label>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.22em] text-white/55 mb-2">Max uses</span>
            <input
              type="number"
              min="1"
              value={maxUses}
              onChange={(e) => setMaxUses(e.target.value)}
              data-testid="coupon-max-input"
              placeholder="Unlimited"
              className="w-full bg-ink-800 border border-white/10 text-white px-4 py-3 text-sm rounded-sm outline-none focus:border-gold"
            />
          </label>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.22em] text-white/55 mb-2">Expires on</span>
            <input
              type="date"
              value={expires}
              onChange={(e) => setExpires(e.target.value)}
              data-testid="coupon-expires-input"
              className="w-full bg-ink-800 border border-white/10 text-white px-4 py-3 text-sm rounded-sm outline-none focus:border-gold"
            />
          </label>
        </div>

        <div className="mt-7 flex items-center gap-3">
          <button
            type="submit"
            disabled={saving}
            data-testid="coupon-create-submit"
            className="btn-gold flex-1 justify-center disabled:opacity-40"
          >
            {saving ? "Creating…" : "Create coupon"}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="btn-ghost !py-3 !px-4"
          >Cancel</button>
        </div>
      </form>
    </div>
  );
}
